/**
 * Strategies / automation client for Oroboros Terminal.
 * Auth: terminal session cookie (+ optional Bearer) — same as Agent chat.
 * Endpoints: MiniApp my-strategies + picker catalog + public top-backtests browse.
 */

import { signedPercentLabel } from '../signedPercent';
import { oroborosJson, oroborosAuthHeaders } from './client';
import { humanizeAgentApiError } from './agentChat';
import { interpretOroMutateApiResponse } from './tradeResponseQueue';

export type OroStrategyMode = 'full_auto' | 'one_click' | 'unknown';

export type OroStrategyPair = {
  pair: string;
  timeframe?: string;
  started: boolean;
  paramHash8?: string;
  pnlPct?: number;
};

export type OroStrategyPerformance = {
  totalTrades: number;
  winningTrades?: number;
  winRate: number;
  totalPnLPct: number;
  totalPnLUsd?: number;
  source?: 'live' | 'backtest';
};

export type OroStrategyRow = {
  id: string;
  name: string;
  isActive: boolean;
  mode: OroStrategyMode;
  /** Perp execution venue for this subscription (hyperliquid | propr). */
  exchange?: 'hyperliquid' | 'propr';
  pairs: OroStrategyPair[];
  performance?: OroStrategyPerformance;
};

export type OroStrategiesStats = {
  activeStrategies: number;
  totalStrategies: number;
  totalPairs: number;
  activePairs: number;
  totalTrades: number;
  overallWinRate: number;
  combinedPnL: number;
  combinedPnLUsd: number;
  avgStrategyPnL: number;
};

export type OroMyStrategiesResult = {
  success: boolean;
  unauthorized?: boolean;
  setupRequired?: boolean;
  message?: string;
  warning?: string;
  directionGate?: string;
  stats?: OroStrategiesStats;
  strategies: OroStrategyRow[];
  error?: string;
};

export type OroTopBacktestRow = {
  id: string;
  name: string;
  pair: string;
  timeframe: string;
  totalReturn: number;
  winRate: number;
  totalTrades: number;
  maxDrawdown?: number;
  profitFactor?: number;
  sharpeRatio?: number;
  days?: number;
  source?: string;
  sparkline?: number[];
  computedAt?: number;
  isFresh?: boolean;
  freshnessStatus?: string;
  paramHash?: string;
  isGoodOrExcellent?: boolean;
  /** True when a cached/optimized backtest cell exists for this strategy. */
  hasBacktest?: boolean;
};

export type OroMyStrategiesAction =
  | { type: 'pause_all' }
  | { type: 'start_all' }
  | { type: 'remove_all' }
  | { type: 'toggle_strategy'; strategyId: string }
  | { type: 'set_mode'; strategyId: string; mode: 'full_auto' | 'one_click' }
  | { type: 'toggle_pair'; strategyId: string; pair: string }
  | { type: 'add_pair'; strategyId: string; pair: string; timeframe?: string }
  | { type: 'remove_pair'; strategyId: string; pair: string }
  | { type: 'set_venue'; strategyId: string; venue: 'hyperliquid' | 'propr' };

function normMode(raw: unknown): OroStrategyMode {
  const s = String(raw || '')
    .trim()
    .toLowerCase();
  if (s === 'full_auto' || s === 'full-auto' || s === 'auto') return 'full_auto';
  if (s === 'one_click' || s === 'one-click' || s === 'semi') return 'one_click';
  return 'unknown';
}

function mapPair(p: any): OroStrategyPair | null {
  const pair = String(p?.pair || '').trim();
  if (!pair) return null;
  const pnlRaw = p?.pnlPct;
  const pnlPct = Number.isFinite(Number(pnlRaw)) ? Number(pnlRaw) : undefined;
  return {
    pair,
    timeframe: p?.timeframe ? String(p.timeframe) : undefined,
    started: p?.started !== false,
    paramHash8: p?.paramHash8 ? String(p.paramHash8) : undefined,
    ...(pnlPct != null ? { pnlPct } : {}),
  };
}

function mapPerformance(raw: any): OroStrategyPerformance | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const totalTrades = Number(raw.totalTrades || 0) || 0;
  const winRate = Number(raw.winRate || 0) || 0;
  const totalPnLPct = Number(raw.totalPnLPct ?? raw.totalPnL ?? 0) || 0;
  const totalPnLUsd = Number.isFinite(Number(raw.totalPnLUsd))
    ? Number(raw.totalPnLUsd)
    : undefined;
  const winningTrades = Number.isFinite(Number(raw.winningTrades))
    ? Number(raw.winningTrades)
    : undefined;
  const source =
    raw.source === 'live' || raw.source === 'backtest' ? raw.source : undefined;
  return { totalTrades, winningTrades, winRate, totalPnLPct, totalPnLUsd, source };
}

function normVenue(raw: unknown): 'hyperliquid' | 'propr' {
  const s = String(raw || '').trim().toLowerCase();
  return s === 'propr' ? 'propr' : 'hyperliquid';
}

function mapStrategy(raw: any): OroStrategyRow | null {
  const id = String(raw?.id || '').trim();
  if (!id) return null;
  const pairs = (Array.isArray(raw?.pairs) ? raw.pairs : [])
    .map(mapPair)
    .filter((p: OroStrategyPair | null): p is OroStrategyPair => p != null);
  return {
    id,
    name: String(raw?.name || id),
    isActive: !!raw?.isActive,
    mode: normMode(raw?.mode),
    exchange: normVenue(raw?.exchange),
    pairs,
    performance: mapPerformance(raw?.performance),
  };
}

function mapStats(raw: any): OroStrategiesStats | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  return {
    activeStrategies: Number(raw.activeStrategies || 0) || 0,
    totalStrategies: Number(raw.totalStrategies || 0) || 0,
    totalPairs: Number(raw.totalPairs || 0) || 0,
    activePairs: Number(raw.activePairs || 0) || 0,
    totalTrades: Number(raw.totalTrades || 0) || 0,
    overallWinRate: Number(raw.overallWinRate || 0) || 0,
    combinedPnL: Number(raw.combinedPnL || 0) || 0,
    combinedPnLUsd: Number(raw.combinedPnLUsd || 0) || 0,
    avgStrategyPnL: Number(raw.avgStrategyPnL || 0) || 0,
  };
}

/** Derive stack KPIs from rows when the API omits `stats` (or for aged preview). */
export function summarizeStrategyRows(rows: OroStrategyRow[]): OroStrategiesStats {
  const totalStrategies = rows.length;
  const activeStrategies = rows.filter((s) => s.isActive).length;
  let totalPairs = 0;
  let activePairs = 0;
  let totalTrades = 0;
  let combinedPnL = 0;
  let combinedPnLUsd = 0;
  let winRateWeight = 0;
  for (const s of rows) {
    totalPairs += s.pairs.length;
    activePairs += s.pairs.filter((p) => p.started).length;
    const perf = s.performance;
    if (!perf || perf.source !== 'live') continue;
    const trades = perf.totalTrades || 0;
    totalTrades += trades;
    combinedPnL += perf.totalPnLPct || 0;
    combinedPnLUsd += perf.totalPnLUsd || 0;
    if (trades > 0) winRateWeight += perf.winRate * trades;
  }
  return {
    activeStrategies,
    totalStrategies,
    totalPairs,
    activePairs,
    totalTrades,
    overallWinRate: totalTrades > 0 ? winRateWeight / totalTrades : 0,
    combinedPnL,
    combinedPnLUsd,
    avgStrategyPnL: totalStrategies ? combinedPnL / totalStrategies : 0,
  };
}

/**
 * Subscribed automation stack — `POST /api/miniapp/automation/my-strategies`.
 * `fast: true` skips slow live enrich (structural + cached perf).
 */
export async function fetchOroborosMyStrategies(opts?: {
  fast?: boolean;
  page?: number;
  /** Bypass server view cache (use after HL setup completes or first auth paint). */
  fresh?: boolean;
}): Promise<OroMyStrategiesResult> {
  const fast = opts?.fast !== false;
  const { ok, status, data } = await oroborosJson<{
    success?: boolean;
    setupRequired?: boolean;
    message?: string;
    warning?: string;
    directionGate?: unknown;
    stats?: any;
    strategies?: any[];
    error?: string;
  }>('/api/miniapp/automation/my-strategies', {
    method: 'POST',
    headers: oroborosAuthHeaders(),
    body: JSON.stringify({
      page: opts?.page ?? 1,
      ...(fast ? { fast: true } : {}),
      ...(opts?.fresh ? { fresh: true } : {}),
    }),
  });

  if (status === 401 || status === 403) {
    return {
      success: false,
      unauthorized: status === 401,
      strategies: [],
      error: humanizeAgentApiError(data?.error || data?.message || `HTTP ${status}`),
    };
  }

  if (!ok || data?.success === false) {
    return {
      success: false,
      strategies: [],
      error: humanizeAgentApiError(data?.error || data?.message || 'Failed to load strategies'),
    };
  }

  const strategies = (Array.isArray(data.strategies) ? data.strategies : [])
    .map(mapStrategy)
    .filter((s): s is OroStrategyRow => s != null);

  return {
    success: true,
    setupRequired: !!data.setupRequired,
    message: data.message ? String(data.message) : undefined,
    warning: data.warning ? String(data.warning) : undefined,
    directionGate: data.directionGate != null ? String(data.directionGate) : undefined,
    stats: mapStats(data.stats),
    strategies,
  };
}

/** Mutate subscribed stack — `POST /api/miniapp/automation/my-strategies/dispatch`. */
export async function dispatchOroborosMyStrategies(
  action: OroMyStrategiesAction,
): Promise<{ success: boolean; error?: string; unauthorized?: boolean }> {
  const { ok, status, data } = await oroborosJson<{
    success?: boolean;
    error?: string;
    message?: string;
  }>('/api/miniapp/automation/my-strategies/dispatch', {
    method: 'POST',
    headers: oroborosAuthHeaders(),
    body: JSON.stringify({ action }),
  });

  const interpreted = interpretOroMutateApiResponse(ok, status, data, {
    rejectQueued: false,
    fallbackError: 'Automation action failed',
    networkError: 'Network error — automation action failed',
  });
  if (!interpreted.success) {
    return {
      success: false,
      unauthorized: interpreted.unauthorized,
      error: humanizeAgentApiError(interpreted.error || 'Automation action failed'),
    };
  }

  return { success: true };
}

function mapSparkline(raw: unknown): number[] | undefined {
  if (!Array.isArray(raw) || raw.length < 2) return undefined;
  const values = raw.map((v) => Number(v)).filter((v) => Number.isFinite(v));
  return values.length >= 2 ? values : undefined;
}

function mapTopBacktestApiRow(r: any): OroTopBacktestRow | null {
  const id = String(r?.id || '').trim();
  if (!id) return null;
  const totalTrades = Number(r?.totalTrades || 0) || 0;
  const sparkline = mapSparkline(r?.sparkline);
  const profitFactor = Number(r?.profitFactor);
  const sharpeRatio = Number(r?.sharpeRatio);
  const days = Number(r?.days);
  const computedAt = Number(r?.computedAt);
  return {
    id,
    name: String(r?.name || r?.id || 'Strategy'),
    pair: String(r?.pair || 'BTC-USD'),
    timeframe: String(r?.timeframe || '5m'),
    totalReturn: Number(r?.totalReturn || 0) || 0,
    winRate: Number(r?.winRate || 0) || 0,
    totalTrades,
    maxDrawdown: Number.isFinite(Number(r?.maxDrawdown)) ? Number(r.maxDrawdown) : undefined,
    profitFactor: Number.isFinite(profitFactor) && profitFactor > 0 ? profitFactor : undefined,
    sharpeRatio: Number.isFinite(sharpeRatio) ? sharpeRatio : undefined,
    days: Number.isFinite(days) && days > 0 ? days : undefined,
    source: r?.source ? String(r.source) : undefined,
    ...(sparkline ? { sparkline } : {}),
    computedAt: Number.isFinite(computedAt) && computedAt > 0 ? computedAt : undefined,
    isFresh: r?.isFresh === true,
    freshnessStatus: r?.freshnessStatus ? String(r.freshnessStatus) : undefined,
    paramHash: r?.paramHash ? String(r.paramHash) : undefined,
    isGoodOrExcellent: !!r?.isGoodOrExcellent,
    hasBacktest: totalTrades > 0 || Number(r?.totalReturn || 0) !== 0 || !!r?.paramHash,
  };
}

function applyBrowseQualityFilter(
  rows: OroTopBacktestRow[],
  filter: 'all' | 'good' | 'excellent',
): OroTopBacktestRow[] {
  if (filter === 'all') return rows;
  let next = rows.filter((r) => r.isGoodOrExcellent);
  if (filter === 'excellent') {
    next = next.filter((r) => r.totalReturn >= 15 && r.winRate >= 60);
  }
  return next;
}

/** Public research browse — `GET /api/strategies/top-backtests`. */
const TOP_CACHE_MS = 90_000;
let topBacktestsCache: {
  filter: string;
  limit: number;
  sparkline: boolean;
  rows: OroTopBacktestRow[];
  at: number;
} | null = null;

function topBacktestsCacheFresh(filter: string): boolean {
  return !!(
    topBacktestsCache &&
    topBacktestsCache.filter === filter &&
    Date.now() - topBacktestsCache.at < TOP_CACHE_MS
  );
}

function topBacktestsCacheCovers(filter: string, limit: number, sparkline: boolean): boolean {
  if (!topBacktestsCacheFresh(filter) || !topBacktestsCache) return false;
  if (topBacktestsCache.limit < limit) return false;
  if (sparkline && !topBacktestsCache.sparkline) return false;
  return true;
}

function topBacktestsFlightKey(filter: string, limit: number, sparkline: boolean): string {
  return `${filter}|${limit}|${sparkline ? '1' : '0'}`;
}

const topBacktestsInFlight = new Map<
  string,
  Promise<{ success: boolean; rows: OroTopBacktestRow[]; error?: string }>
>();

function topRowKey(row: Pick<OroTopBacktestRow, 'id' | 'pair' | 'timeframe'>): string {
  return `${row.id}|${row.pair}|${row.timeframe}`;
}

/** Overlay sparkline / hash from a slower hydrate onto the already-painted ranked board. */
export function mergeOroborosSparklineRows(
  current: OroTopBacktestRow[],
  incoming: OroTopBacktestRow[],
): OroTopBacktestRow[] {
  if (!incoming.length) return current;
  if (!current.length) return incoming;
  const nextByKey = new Map<string, OroTopBacktestRow>();
  for (const row of incoming) nextByKey.set(topRowKey(row), row);
  return current.map((row) => {
    const next = nextByKey.get(topRowKey(row));
    if (!next) return row;
    const sparkline = next.sparkline?.length ? next.sparkline : row.sparkline;
    const paramHash = next.paramHash || row.paramHash;
    if (sparkline === row.sparkline && paramHash === row.paramHash) return row;
    return {
      ...row,
      ...(sparkline ? { sparkline } : {}),
      ...(paramHash ? { paramHash } : {}),
    };
  });
}

export function peekOroborosTopBacktests(
  filter: 'all' | 'good' | 'excellent' = 'good',
  limit = 48,
): OroTopBacktestRow[] | null {
  if (!topBacktestsCacheCovers(filter, limit, false)) return null;
  return topBacktestsCache!.rows.slice(0, limit);
}

export async function fetchOroborosTopBacktests(opts?: {
  filter?: 'all' | 'good' | 'excellent';
  limit?: number;
  sparkline?: boolean;
}): Promise<{ success: boolean; rows: OroTopBacktestRow[]; error?: string }> {
  const filter = opts?.filter || 'good';
  const limit = Math.max(1, Math.min(500, opts?.limit ?? 48));
  const sparkline = opts?.sparkline === true;
  if (topBacktestsCacheCovers(filter, limit, sparkline)) {
    return { success: true, rows: topBacktestsCache!.rows.slice(0, limit) };
  }
  const flightKey = topBacktestsFlightKey(filter, limit, sparkline);
  const inFlight = topBacktestsInFlight.get(flightKey);
  if (inFlight) return inFlight;

  const pending = (async () => {
    const qs = new URLSearchParams({
      filter,
      limit: String(limit),
      ...(sparkline ? { sparkline: '1' } : {}),
    });
    const { ok, data } = await oroborosJson<{
      success?: boolean;
      strategies?: any[];
      message?: string;
    }>(`/api/strategies/top-backtests?${qs}`);

    if (!ok || data?.success === false) {
      return {
        success: false,
        rows: [] as OroTopBacktestRow[],
        error: data?.message || 'Failed to load top backtests',
      };
    }

    const rows: OroTopBacktestRow[] = (Array.isArray(data.strategies) ? data.strategies : [])
      .slice(0, limit)
      .map(mapTopBacktestApiRow)
      .filter((r): r is OroTopBacktestRow => !!r);

    const richerCache =
      topBacktestsCacheFresh(filter) &&
      topBacktestsCache &&
      (topBacktestsCache.limit > limit || (topBacktestsCache.sparkline && !sparkline));
    if (!richerCache) {
      topBacktestsCache = { filter, limit, sparkline, rows, at: Date.now() };
    }
    return { success: true, rows };
  })().finally(() => {
    topBacktestsInFlight.delete(flightKey);
  });

  topBacktestsInFlight.set(flightKey, pending);
  return pending;
}

/** Warm ranked-board metrics. Sparklines are a second, slower pass. */
export function prefetchOroborosTopBacktests(
  filter: 'all' | 'good' | 'excellent' = 'good',
  limit = 250,
  sparkline = false,
): void {
  void fetchOroborosTopBacktests({ filter, limit, sparkline });
}

/** Test-only: drop in-memory top-backtests cache + in-flight map. */
export function resetOroborosTopBacktestsCacheForTests(): void {
  topBacktestsCache = null;
  topBacktestsInFlight.clear();
}

/**
 * Full Strategies browse — picker catalog hydrated with top-backtest metrics.
 * Unlike `fetchOroborosTopBacktests`, includes strategies with no cached backtest yet.
 */
const BROWSE_CACHE_MS = 90_000;
const BROWSE_PREFETCH_METRICS_LIMIT = 120;
let browseCache: { filter: string; limit: number; rows: OroTopBacktestRow[]; at: number } | null = null;

/** Warm browse catalog in the background (no-op if cache is fresh). */
export function prefetchOroborosStrategyBrowse(
  filter: 'all' | 'good' | 'excellent' = 'all',
  metricsLimit = BROWSE_PREFETCH_METRICS_LIMIT,
): void {
  void fetchOroborosStrategyBrowse({ filter, metricsLimit });
}

export async function fetchOroborosStrategyBrowse(opts?: {
  filter?: 'all' | 'good' | 'excellent';
  /** Top-backtests rows to hydrate (default 500 for full browse). */
  metricsLimit?: number;
}): Promise<{ success: boolean; rows: OroTopBacktestRow[]; error?: string }> {
  const filter = opts?.filter || 'all';
  const metricsLimit = Math.max(1, Number(opts?.metricsLimit ?? 500) || 500);
  if (
    browseCache &&
    browseCache.filter === filter &&
    browseCache.limit >= metricsLimit &&
    Date.now() - browseCache.at < BROWSE_CACHE_MS
  ) {
    return { success: true, rows: browseCache.rows };
  }
  const topQs = new URLSearchParams({ filter: 'all', limit: String(metricsLimit), sparkline: '1' });

  const [pickerRes, topRes] = await Promise.all([
    oroborosJson<{
      success?: boolean;
      strategies?: any[];
      message?: string;
    }>('/api/available-strategies/picker'),
    oroborosJson<{
      success?: boolean;
      strategies?: any[];
      message?: string;
    }>(`/api/strategies/top-backtests?${topQs}`),
  ]);

  if (!pickerRes.ok || pickerRes.data?.success === false) {
    return {
      success: false,
      rows: [],
      error: pickerRes.data?.message || 'Failed to load strategy catalog',
    };
  }

  const metricsById = new Map<string, OroTopBacktestRow>();
  if (topRes.ok && topRes.data?.success !== false) {
    for (const raw of Array.isArray(topRes.data?.strategies) ? topRes.data.strategies : []) {
      const mapped = mapTopBacktestApiRow(raw);
      if (mapped) metricsById.set(mapped.id, mapped);
    }
  }

  const seen = new Set<string>();
  const rows: OroTopBacktestRow[] = [];
  for (const s of Array.isArray(pickerRes.data?.strategies) ? pickerRes.data.strategies : []) {
    const id = String(s?.id || '').trim();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    const metrics = metricsById.get(id);
    const name = String(s?.displayName || s?.name || metrics?.name || id).trim() || id;
    const timeframe = String(
      metrics?.timeframe || s?.timeframe || s?.defaultTimeframe || '5m',
    ).trim() || '5m';
    rows.push(
      metrics
        ? { ...metrics, name, timeframe: metrics.timeframe || timeframe }
        : {
            id,
            name,
            pair: 'BTC-USD',
            timeframe,
            totalReturn: 0,
            winRate: 0,
            totalTrades: 0,
            isGoodOrExcellent: false,
            hasBacktest: false,
          },
    );
  }

  // Keep any top-backtest rows missing from picker (rare / cache edge).
  for (const metrics of metricsById.values()) {
    if (seen.has(metrics.id)) continue;
    rows.push(metrics);
  }

  const filtered = applyBrowseQualityFilter(rows, filter);
  browseCache = { filter, limit: metricsLimit, rows: filtered, at: Date.now() };
  return { success: true, rows: filtered };
}

export type OroOptimizerPick = {
  strategyId: string;
  strategyName: string;
  rankWithinStrategy: number;
  globalRank: number;
  parameters: Record<string, unknown>;
  score: number;
  totalReturn?: number;
  winRate?: number;
  maxDrawdown?: number;
  totalTrades?: number;
  paramsHash8?: string;
};

/** Optimizer combos — `GET /api/strategies/top-optimized-combos`. */
export async function fetchOroborosOptimizerPicks(opts: {
  pair: string;
  timeframe: string;
  days?: number;
  filter?: 'good' | 'all';
  limit?: number;
}): Promise<{
  success: boolean;
  combos: OroOptimizerPick[];
  pair: string;
  timeframe: string;
  days: number;
  error?: string;
}> {
  const pair = String(opts.pair || 'BTC-USD').trim() || 'BTC-USD';
  const timeframe = String(opts.timeframe || '5m').trim() || '5m';
  const days = opts.days ?? 30;
  const filter = opts.filter || 'good';
  const limit = Math.max(1, Math.min(120, opts.limit ?? 60));
  const qs = new URLSearchParams({
    pair,
    timeframe,
    days: String(days),
    perStrategy: '3',
    limit: String(limit),
    filter,
  });
  const { ok, data } = await oroborosJson<{
    success?: boolean;
    pair?: string;
    timeframe?: string;
    days?: number;
    combos?: any[];
    message?: string;
  }>(`/api/strategies/top-optimized-combos?${qs}`);

  if (!ok || data?.success === false) {
    return {
      success: false,
      combos: [],
      pair,
      timeframe,
      days,
      error: data?.message || 'Failed to load optimizer picks',
    };
  }

  const combos: OroOptimizerPick[] = (Array.isArray(data.combos) ? data.combos : [])
    .map((c: any) => ({
      strategyId: String(c?.strategyId || '').trim(),
      strategyName: String(c?.strategyName || c?.strategyId || 'Strategy'),
      rankWithinStrategy: Number(c?.rankWithinStrategy || 0) || 0,
      globalRank: Number(c?.globalRank || 0) || 0,
      parameters: c?.parameters && typeof c.parameters === 'object' ? c.parameters : {},
      score: Number(c?.score || 0) || 0,
      totalReturn: Number.isFinite(Number(c?.totalReturn)) ? Number(c.totalReturn) : undefined,
      winRate: Number.isFinite(Number(c?.winRate)) ? Number(c.winRate) : undefined,
      maxDrawdown: Number.isFinite(Number(c?.maxDrawdown)) ? Number(c.maxDrawdown) : undefined,
      totalTrades: Number.isFinite(Number(c?.totalTrades)) ? Number(c.totalTrades) : undefined,
      paramsHash8: c?.paramsHash8 ? String(c.paramsHash8) : undefined,
    }))
    .filter((c) => !!c.strategyId);

  return {
    success: true,
    combos,
    pair: String(data.pair || pair),
    timeframe: String(data.timeframe || timeframe),
    days: Number(data.days) || days,
  };
}

/** Subscribe a strategy — `POST /api/miniapp/automation/subscribe`. */
export async function subscribeOroborosStrategy(opts: {
  strategyId: string;
  pairs: string[];
  timeframe: string;
  activateTrading?: boolean;
  venue?: 'hyperliquid' | 'propr';
}): Promise<{ success: boolean; error?: string; unauthorized?: boolean }> {
  const { ok, status, data } = await oroborosJson<{
    success?: boolean;
    error?: string;
    message?: string;
  }>('/api/miniapp/automation/subscribe', {
    method: 'POST',
    headers: oroborosAuthHeaders(),
    body: JSON.stringify({
      strategyId: opts.strategyId,
      pairs: opts.pairs,
      timeframe: opts.timeframe,
      activateTrading: opts.activateTrading !== false,
      ...(opts.venue ? { venue: opts.venue } : {}),
    }),
  });

  const interpreted = interpretOroMutateApiResponse(ok, status, data, {
    rejectQueued: false,
    fallbackError: 'Subscribe failed',
    networkError: 'Network error — subscribe failed',
  });
  if (!interpreted.success) {
    return {
      success: false,
      unauthorized: interpreted.unauthorized,
      error: humanizeAgentApiError(interpreted.error || 'Subscribe failed'),
    };
  }

  return { success: true };
}

export type OroAutomationGlobalSettings = {
  directionGate: 'long' | 'short' | 'neutral';
  defaultAutomationMode: 'full_auto' | 'one_click';
  globalPositionStacking: boolean;
  quietSignalNotifications: boolean;
  privacyHideOrderIds: boolean;
  automationMaxLossPerTrade?: { mode: 'percent' | 'usd'; value: number } | null;
};

export type OroStackSettingsDraft = {
  directionGate: 'long' | 'short' | 'neutral';
  defaultAutomationMode: 'full_auto' | 'one_click';
  globalPositionStacking: boolean;
  quietSignalNotifications: boolean;
  privacyHideOrderIds: boolean;
  maxLossMode: 'percent' | 'usd';
  maxLossValue: string;
};

/** Stack-wide defaults — `POST /api/miniapp/automation/global-settings`. */
export async function fetchOroborosAutomationGlobalSettings(): Promise<{
  success: boolean;
  settings: OroAutomationGlobalSettings | null;
  error?: string;
  unauthorized?: boolean;
}> {
  const { ok, status, data } = await oroborosJson<
    OroAutomationGlobalSettings & { success?: boolean; error?: string }
  >('/api/miniapp/automation/global-settings', {
    method: 'POST',
    headers: oroborosAuthHeaders(),
    body: JSON.stringify({}),
  });
  if (status === 401 || status === 403) {
    return {
      success: false,
      settings: null,
      unauthorized: status === 401,
      error: humanizeAgentApiError(data?.error || `HTTP ${status}`),
    };
  }
  if (!ok || data?.success === false) {
    return {
      success: false,
      settings: null,
      error: humanizeAgentApiError(data?.error || 'Failed to load stack settings'),
    };
  }
  return {
    success: true,
    settings: {
      directionGate:
        data.directionGate === 'long' || data.directionGate === 'short'
          ? data.directionGate
          : 'neutral',
      defaultAutomationMode:
        data.defaultAutomationMode === 'one_click' ? 'one_click' : 'full_auto',
      globalPositionStacking: !!data.globalPositionStacking,
      quietSignalNotifications: !!data.quietSignalNotifications,
      privacyHideOrderIds: !!data.privacyHideOrderIds,
      automationMaxLossPerTrade: data.automationMaxLossPerTrade ?? null,
    },
  };
}

export async function saveOroborosAutomationGlobalSettings(
  draft: OroStackSettingsDraft,
): Promise<{ success: boolean; settings: OroAutomationGlobalSettings | null; error?: string }> {
  const value = Number(draft.maxLossValue);
  if (!Number.isFinite(value) || value <= 0) {
    return { success: false, settings: null, error: 'Enter a valid max loss amount' };
  }
  const { ok, status, data } = await oroborosJson<
    OroAutomationGlobalSettings & { success?: boolean; error?: string }
  >('/api/miniapp/automation/global-settings', {
    method: 'POST',
    headers: oroborosAuthHeaders(),
    body: JSON.stringify({
      defaultAutomationMode: draft.defaultAutomationMode,
      globalPositionStacking: draft.globalPositionStacking,
      quietSignalNotifications: draft.quietSignalNotifications,
      privacyHideOrderIds: draft.privacyHideOrderIds,
      automationMaxLossPerTrade: {
        mode: draft.maxLossMode === 'usd' ? 'usd' : 'percent',
        value,
      },
    }),
  });
  const interpreted = interpretOroMutateApiResponse(ok, status, data, {
    rejectQueued: false,
    fallbackError: 'Failed to save stack settings',
    networkError: 'Network error — failed to save stack settings',
  });
  if (!interpreted.success) {
    return {
      success: false,
      settings: null,
      error: humanizeAgentApiError(interpreted.error || 'Failed to save stack settings'),
    };
  }
  return {
    success: true,
    settings: {
      directionGate:
        data.directionGate === 'long' || data.directionGate === 'short'
          ? data.directionGate
          : draft.directionGate,
      defaultAutomationMode:
        data.defaultAutomationMode === 'one_click' ? 'one_click' : 'full_auto',
      globalPositionStacking: !!data.globalPositionStacking,
      quietSignalNotifications: !!data.quietSignalNotifications,
      privacyHideOrderIds: !!data.privacyHideOrderIds,
      automationMaxLossPerTrade: data.automationMaxLossPerTrade ?? {
        mode: draft.maxLossMode === 'usd' ? 'usd' : 'percent',
        value,
      },
    },
  };
}

export function formatPct(n: number | undefined | null, digits = 1): string {
  if (n == null || !Number.isFinite(n)) return '—';
  return `${signedPercentLabel(n, digits, true)}%`;
}

export function formatModeLabel(mode: OroStrategyMode): string {
  if (mode === 'full_auto') return 'Full auto';
  if (mode === 'one_click') return 'One-click';
  return '—';
}
